/**
 * Phase VII.L.1 · Onboarding tour · 3 steps · V12 Achromatic Strict.
 *
 * Hook `useOnboardingTour`:
 *   - loads `/api/v2/user-prefs/me`, triggers tour when
 *     `dismissed['onboarding_tour_phase_vii_v1']` is missing
 *   - also triggers on `?demo=1` query param (explicit opt-in)
 *   - persists dismiss via POST `/api/v2/user-prefs/dismiss`
 *
 * Component `OnboardingTour`:
 *   - renders a fixed overlay with 3 cards: welcome, agents CTA,
 *     suites CTA
 *   - uses Framer Motion-free CSS transitions (keeps bundle lean)
 *   - never saturated colors · neutral/slate only
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { X, Sparkles, Package, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";

const FEATURE_ID = "onboarding_tour_phase_vii_v1";


async function _j(path, opts = {}) {
    try {
        const r = await fetch(`${API}${path}`,
            { credentials: "include", ...opts });
        const d = await r.json().catch(() => ({}));
        return { ok: r.ok, status: r.status, data: d };
    } catch (e) {
        return { ok: false, status: 0, data: {} };
    }
}


export function useOnboardingTour() {
    const [open, setOpen] = useState(false);
    const [checked, setChecked] = useState(false);
    const [params] = useSearchParams();

    useEffect(() => {
        let alive = true;
        async function check() {
            // Explicit opt-in via query param always wins
            if (params.get("demo") === "1") {
                if (alive) { setOpen(true); setChecked(true); }
                return;
            }
            const r = await _j("/v2/user-prefs/me");
            if (!alive) return;
            setChecked(true);
            const dismissed = r.data?.dismissed || {};
            if (!dismissed[FEATURE_ID]) setOpen(true);
        }
        check();
        return () => { alive = false; };
    }, [params]);

    const dismiss = useCallback(async () => {
        setOpen(false);
        await _j("/v2/user-prefs/dismiss", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ feature_id: FEATURE_ID }),
        });
    }, []);

    return { open, dismiss, checked };
}


export function OnboardingTour({ open, onDismiss }) {
    const [step, setStep] = useState(0);
    const nav = useNavigate();

    if (!open) return null;

    const steps = [
        {
            key: "welcome",
            icon: <Sparkles size={18} className="stroke-neutral-300" />,
            title: "Welcome to GlobalLeadSuite",
            body: "The AI-first lead generation platform. In 60 seconds you'll see how to try your first agent and explore Suite bundles.",
            cta: "Next",
            onClick: () => setStep(1),
        },
        {
            key: "agents",
            icon: <Sparkles size={18} className="stroke-neutral-300" />,
            title: "Try your first AI agent in 1 click",
            body: "Head to AI Agents and tap `Try with sample data`. No setup, no payload — just a working run so you can see the output.",
            cta: "Open AI Agents",
            onClick: () => {
                onDismiss();
                nav("/app/ai/agents?demo=1");
            },
        },
        {
            key: "suites",
            icon: <Package size={18} className="stroke-neutral-300" />,
            title: "Explore Suite bundles",
            body: "Bundles pack multiple agents at a discount. Browse the catalog and unlock a complete workflow in one purchase.",
            cta: "Open Suites",
            onClick: () => {
                onDismiss();
                nav("/app/suites");
            },
        },
    ];
    const s = steps[step] || steps[0];

    return (
        <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70]"
            data-testid="onboarding-tour-overlay">
            <div className="relative w-[520px] max-w-[92vw] rounded-lg
                            border border-neutral-800 bg-neutral-950 p-6
                            text-neutral-100 shadow-2xl">
                <button
                    onClick={onDismiss}
                    className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-200"
                    data-testid="onboarding-tour-close">
                    <X size={16} />
                </button>
                <div className="flex items-center gap-2 mb-3">
                    {s.icon}
                    <span className="text-[11px] text-neutral-500 uppercase tracking-wider">
                        Step {step + 1} / {steps.length}
                    </span>
                </div>
                <h2 className="text-xl font-semibold tracking-tight"
                    data-testid={`onboarding-tour-title-${s.key}`}>
                    {s.title}
                </h2>
                <p className="mt-3 text-sm text-neutral-400 leading-relaxed">
                    {s.body}
                </p>
                <div className="mt-6 flex items-center justify-between">
                    <div className="flex gap-1.5">
                        {steps.map((_, i) => (
                            <span key={i}
                                className={`h-1.5 w-6 rounded-full transition
                                            ${i === step ? "bg-neutral-100" : "bg-neutral-800"}`} />
                        ))}
                    </div>
                    <div className="flex gap-2">
                        {step > 0 && (
                            <button
                                onClick={() => setStep(step - 1)}
                                className="text-xs px-3 py-2 rounded border border-neutral-800 text-neutral-400 hover:bg-neutral-900"
                                data-testid="onboarding-tour-back">
                                Back
                            </button>
                        )}
                        <button
                            onClick={s.onClick}
                            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded bg-neutral-100 text-neutral-900 hover:bg-white font-medium"
                            data-testid={`onboarding-tour-cta-${s.key}`}>
                            {s.cta} <ArrowRight size={12} />
                        </button>
                    </div>
                </div>
                <button
                    onClick={onDismiss}
                    className="mt-5 w-full text-[11px] text-neutral-600 hover:text-neutral-400"
                    data-testid="onboarding-tour-skip">
                    Skip tour · don't show again
                </button>
            </div>
        </div>
    );
}


export function OnboardingTourMount() {
    const { open, dismiss } = useOnboardingTour();
    return <OnboardingTour open={open} onDismiss={dismiss} />;
}


export default OnboardingTourMount;
